import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/pixel";

const TIPOS = ["Factura original", "Refacturado", "Seminuevo"];

export default function Sell() {
  const [form, setForm] = useState({
    marca: "", modelo: "", anio: "", version: "", kilometraje: "",
    precio_deseado: "", tipo_documentacion: "Factura original", num_duenos: "",
    origen_placas: "", nombre_contacto: "", telefono_contacto: "", correo: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {folio}

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        marca: form.marca.trim(),
        modelo: form.modelo.trim(),
        anio: Number(form.anio),
        version: form.version.trim(),
        kilometraje: Number(form.kilometraje),
        precio_deseado: Number(form.precio_deseado),
        tipo_documentacion: form.tipo_documentacion,
        num_duenos: Number(form.num_duenos),
        origen_placas: form.origen_placas.trim(),
        fotos: [], // photos are gathered by WhatsApp after registration, not uploaded here
        nombre_contacto: form.nombre_contacto.trim(),
        telefono_contacto: form.telefono_contacto.trim(),
        correo: form.correo.trim() || null,
      };
      const { data } = await api.post("/inventory", payload);
      setSubmitted({ folio: data.folio });
      trackLead({ content_name: "Vender mi auto", content_category: "sell" });
      toast.success("Registro enviado. Te contactaremos pronto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white text-[#3E5641]">
        <PublicHeader />
        <main className="container-x pt-40 pb-24">
          <div className="max-w-xl">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Registro recibido</p>
            <h1 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
              Gracias. Tu auto está en la fila.
            </h1>
            <p className="mt-5 text-[#6B7678]">
              Un asesor te contactará por teléfono y por WhatsApp para agendar el cotejo documental
              y pedirte las fotos del vehículo. Guarda este folio:
            </p>
            <p className="mono mt-6 inline-block border border-[#3E5641] px-6 py-3 text-2xl text-[#3E5641]" data-testid="sell-folio">
              {submitted.folio}
            </p>
            <div className="mt-10">
              <Link to="/" className="btn-outline-dark" data-testid="sell-back-home">
                <ArrowLeft size={16} /> Volver al inicio
              </Link>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#3E5641]" data-testid="page-sell">
      <PublicHeader />
      <main className="container-x pt-32 pb-24 md:pt-40">
        <div className="mb-10 max-w-2xl">
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Vender mi auto</p>
          <h1 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
            Registrar tu auto para valuación.
          </h1>
          <p className="mt-5 text-[#6B7678]">
            No pedimos documentos legales aquí. Solo datos básicos y contacto. Las fotos del vehículo
            se solicitan después directamente por WhatsApp, y el cotejo de factura y trámites se hace en persona.
          </p>
        </div>

        <form onSubmit={onSubmit} className="max-w-3xl space-y-8" data-testid="sell-form">
          <div className="grid gap-6 md:grid-cols-2">
            <Field label="Marca" required>
              <Input required value={form.marca} onChange={(e) => setField("marca", e.target.value)} data-testid="sell-marca" />
            </Field>
            <Field label="Modelo" required>
              <Input required value={form.modelo} onChange={(e) => setField("modelo", e.target.value)} data-testid="sell-modelo" />
            </Field>
            <Field label="Año" required>
              <Input type="number" min="1950" max="2035" required value={form.anio} onChange={(e) => setField("anio", e.target.value)} data-testid="sell-anio" />
            </Field>
            <Field label="Versión exacta" required>
              <Input required value={form.version} onChange={(e) => setField("version", e.target.value)} placeholder="Ej. Sport 2.0 TSI" data-testid="sell-version" />
            </Field>
            <Field label="Kilometraje" required>
              <Input type="number" min="0" required value={form.kilometraje} onChange={(e) => setField("kilometraje", e.target.value)} data-testid="sell-km" />
            </Field>
            <Field label="Precio deseado (MXN)" required>
              <Input type="number" min="0" step="1000" required value={form.precio_deseado} onChange={(e) => setField("precio_deseado", e.target.value)} data-testid="sell-precio" />
            </Field>
            <Field label="Tipo de documentación" required>
              <Select value={form.tipo_documentacion} onValueChange={(v) => setField("tipo_documentacion", v)}>
                <SelectTrigger data-testid="sell-tipo-doc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} value={t} data-testid={`sell-tipo-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Número de dueños" required>
              <Input type="number" min="1" required value={form.num_duenos} onChange={(e) => setField("num_duenos", e.target.value)} data-testid="sell-duenos" />
            </Field>
            <Field label="Origen de placas" required>
              <Input required value={form.origen_placas} onChange={(e) => setField("origen_placas", e.target.value)} placeholder="Ej. Ciudad de México" data-testid="sell-placas" />
            </Field>
          </div>

          {/* Contact */}
          <div className="border-t border-[#3E5641]/10 pt-8">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Nombre de contacto" required>
                <Input required value={form.nombre_contacto} onChange={(e) => setField("nombre_contacto", e.target.value)} data-testid="sell-nombre" />
              </Field>
              <Field label="Teléfono" required>
                <Input required value={form.telefono_contacto} onChange={(e) => setField("telefono_contacto", e.target.value)} data-testid="sell-tel" />
              </Field>
              <Field label="Correo (opcional)">
                <Input type="email" value={form.correo} onChange={(e) => setField("correo", e.target.value)} data-testid="sell-correo" />
              </Field>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="sell-submit">
              {submitting ? "Enviando..." : "Enviar registro"}
            </button>
            <Link to="/" className="text-sm text-[#6B7678] hover:text-[#3E5641]">Cancelar</Link>
          </div>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="field-label">
        {label}{required && <span className="ml-1 text-[#A0553F]">*</span>}
      </Label>
      {children}
    </div>
  );
}
